import { useNodesInitialized } from '@xyflow/react';
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { useDocumentVisibility } from '@/hooks/useDocumentVisibility';
import { useEffectReducer } from '@/hooks/useEffectReducer';
import { useGetState } from '@/hooks/useGetState';

import { useEffectsMap } from '../effects/useEffectsMap.hook';
import { GetHouseholdFamilyTreeQuery } from '../graphql/FamilyTree.generated';
import { useFamilyTreeReducer } from '../reducers/familyTree.reducer';
import { generateDefaultState } from '../state/generateDefaultState';
import {
  FamilyTreeAction,
  FamilyTreeEffect,
  FamilyTreeInitialStateGetter,
  FamilyTreeState,
  FamilyTreeStateProps,
} from '../types';
import { FamilyTreeContext } from './familyTree.context';

interface FamilyTreeContextValueProps extends FamilyTreeStateProps {
  familyTreeData?: GetHouseholdFamilyTreeQuery['familyTree'];
  clientProfiles?: GetHouseholdFamilyTreeQuery['clientProfiles'];
  initialSidePanelState?: {
    selectedPersonId?: string;
    isSidePanelOpen: boolean;
    sidePanelVariant?: 'linked' | 'unlinked';
    sidePanelHistory?: string[];
  };
  registerFamilyTreeReady?: (householdId: string) => void;
  onAfterEdit?: () => void;
  initialRenderWhiteLabeled?: boolean;
}

function useFamilyTreeContextValue({
  householdId,
  familyTreeData,
  clientProfiles,
  initialSidePanelState,
  presentationMode,
  registerFamilyTreeReady,
  onAfterEdit,
  initialRenderWhiteLabeled = false,
}: FamilyTreeContextValueProps): FamilyTreeContext {
  const reducer = useFamilyTreeReducer();
  const isNodesInitialized = useNodesInitialized();
  const visibility = useDocumentVisibility();

  const effectsMap = useEffectsMap({ onAfterEdit });

  const getInitialState = useCallback<FamilyTreeInitialStateGetter>(
    (_queueEffect) => {
      const defaultState = {
        ...generateDefaultState({
          householdId,
          familyTreeData,
          clientProfiles,
          presentationMode,
        }),
        renderWhiteLabeled: initialRenderWhiteLabeled,
      };

      // Override with preserved side panel state if provided
      if (initialSidePanelState) {
        return {
          ...defaultState,
          ...initialSidePanelState,
        };
      }

      return defaultState;
    },
    [
      householdId,
      familyTreeData,
      clientProfiles,
      initialSidePanelState,
      presentationMode,
      initialRenderWhiteLabeled,
    ]
  );

  const [state, dispatch] = useEffectReducer<
    FamilyTreeState,
    FamilyTreeAction,
    FamilyTreeEffect
  >(reducer, getInitialState, effectsMap);

  // Watch for changes to familyTreeData and update the state when it changes
  useEffect(() => {
    if (familyTreeData && clientProfiles) {
      dispatch({
        type: 'FAMILY_TREE_DATA_LOADED',
        familyTreeData,
        clientProfiles,
      });
    }
  }, [dispatch, familyTreeData, clientProfiles]);

  // Track readiness and notify when ready (similar to EstateWaterfallProvider)
  const familyTreeReady =
    visibility === 'visible' &&
    isNodesInitialized &&
    !state.isInitializingFlowchart;

  useEffect(() => {
    if (!familyTreeReady) return;

    registerFamilyTreeReady?.(householdId);
  }, [registerFamilyTreeReady, householdId, familyTreeReady]);

  const getState = useGetState(state);

  return useMemo(
    () => ({ state, dispatch, getState, onAfterEdit }),
    [state, dispatch, getState, onAfterEdit]
  );
}

export interface FamilyTreeProviderProps extends FamilyTreeStateProps {
  children: JSX.Element;
  initialSidePanelState?: {
    selectedPersonId?: string;
    isSidePanelOpen: boolean;
    sidePanelVariant?: 'linked' | 'unlinked';
    sidePanelHistory?: string[];
  };
  registerFamilyTreeReady?: (householdId: string) => void;
  onAfterEdit?: () => void;
  /**
   * Initial value for the "Preview in brand style" toggle. Defaults to false
   * so read-only and presentation renders are unaffected; the edit trowser
   * passes true so the toggle defaults on when editing.
   */
  initialRenderWhiteLabeled?: boolean;
}

export const FamilyTreeProvider = ({
  children,
  registerFamilyTreeReady,
  ...props
}: PropsWithChildren<FamilyTreeProviderProps>) => {
  const value = useFamilyTreeContextValue({
    ...props,
    registerFamilyTreeReady,
  });

  // The flow chart requires the tab to be active to render correctly
  // https://github.com/xyflow/xyflow/issues/2636
  const visibility = useDocumentVisibility();
  const isNodesInitialized = useNodesInitialized();
  if (visibility !== 'visible' && !isNodesInitialized) return null;

  return (
    <FamilyTreeContext.Provider value={value}>
      {children}
    </FamilyTreeContext.Provider>
  );
};
